import store from '@/store';
import requestAPI from '../requestBeApi';
import router from '@/router/index';

const assessmentsService = {
  async getAssessments({
    self,
    filter,
    reloadExisting,
  }: {
    self: any;
    filter: string;
    reloadExisting?: boolean;
  }): Promise<void> {
    if (store.state.allAssessmentsDelivered && !reloadExisting) return;

    store.commit('setInterviewsRequestPending', true);

    const currentListLength = store.state.eventsNumber.interviews[filter];
    const defaultCall = !reloadExisting;
    let page: number, perPage: number;

    if (defaultCall) {
      perPage = 10;
      page = store.state.nextAssessmentsPageNum;
    } else if (reloadExisting) {
      perPage = currentListLength + 1;
      page = 1;

      store.commit('cleanInterviews');
    }

    let statuses: string;

    if (filter === 'todoInterviews') statuses = 'status[]=new';
    if (filter === 'ongoingInterviews') {
      const ongoingInterviewsStatuses = [
        'status[]=invited',
        'cancelled_by_candidate',
        'accepted',
        'confirmed',
        'in_progress',
        'snoozed',
      ];

      statuses = ongoingInterviewsStatuses.join('&status[]=');
    }
    if (filter === 'completedInterviews')
      statuses =
        'status[]=cancelled&status[]=assessed&status[]=published&status[]=completed&status[]=expired&status[]=request_expired';

    if (defaultCall)
      store.commit('setNextAssessmentsPageNum', (store.state.nextAssessmentsPageNum += 1));
    const response = await requestAPI({
      url: `/api/v1/assessments?${statuses}&page=${page}&per_page=${perPage}&order[updated_at]=DESC`,
      method: 'GET',
    });

    if (response.ok) {
      this.parseEvents(response, 'interviews', filter);
    } else if (response.status !== 401) self.$message.error(self.$t('someErrorOccurred'));

    store.commit('setInterviewsRequestPending', false);
  },

  async getReports({
    self,
    filter,
    reloadExisting,
  }: {
    self: any;
    filter: string;
    reloadExisting?: boolean;
  }): Promise<void> {
    if (store.state.allReportsDelivered && !reloadExisting) return;

    store.commit('setReportsRequestPending', true);

    const currentListLength = store.state.eventsNumber.reports[filter];
    const defaultCall = !reloadExisting;
    let page: number, perPage: number;

    if (defaultCall) {
      perPage = 10;
      page = store.state.nextReportsPageNum;
    } else if (reloadExisting) {
      perPage = currentListLength + 1;
      page = 1;

      store.commit('cleanReports');
    }

    let statuses: string;

    if (filter === 'todo' && store.state.userRole === 'assessor')
      statuses = 'status[]=assessed&status[]=published';
    if (filter === 'todo' && store.state.userRole === 'client') statuses = 'status[]=published';
    if (filter === 'drafts') statuses = 'status[]=assessed&status[]=published';
    if (filter === 'sent') statuses = 'status[]=assessed&status[]=published';
    if (filter === 'approved') statuses = 'status[]=completed';

    if (defaultCall) store.commit('setNextReportsPageNum', (store.state.nextReportsPageNum += 1));
    const response = await requestAPI({
      url: `/api/v1/assessments?${statuses}&page=${page}&per_page=${perPage}&order[updated_at]=DESC`,
      method: 'GET',
    });

    if (response.ok) {
      this.parseEvents(response, 'reports', filter);
    } else if (response.status !== 401) self.$message.error(self.$t('someErrorOccurred'));

    store.commit('setReportsRequestPending', false);
  },

  async parseEvents(response: { json: () => any }, mode: string, filter: string): Promise<void> {
    const data = await response.json();

    let events: any[] = [];

    // Assessments with existing reports of these statuses can only be seen by assessor in To do and Drafts filters respectively
    const assrOnly = ['rejected', 'draft', 'prepared'];

    data.forEach(
      (assessment: {
        client?: { company_name?: string; function?: string };
        candidate: { first_name: string; surname: string; profile_image: string; id: string };
        percentage: number;
        id: string;
        status: string;
        date: string;
        report: { id: string; status: string };
        assessment_request: { is_virtual: boolean };
      }) => {
        !assessment.client ? (assessment.client = {}) : '';

        const assessmentFin = {
          name: `${assessment.candidate.first_name} ${assessment.candidate.surname}`,
          imgUrl: assessment.candidate.profile_image,
          company: assessment.client.company_name,
          position: assessment.client.function,
          percentageProgress: assessment.percentage,
          candidateId: assessment.candidate.id,
          id: assessment.id,
          status: assessment.status,
          date: assessment.date,
          assessmentObject: assessment,
          reportId: assessment.report ? assessment.report.id : '',
          reportStatus: assessment.report ? assessment.report.status : '',
          isVirtual: assessment.assessment_request.is_virtual,
        };

        const qualifiesAsTodoReport =
          (store.state.userRole === 'client' && !assrOnly.includes(assessmentFin.reportStatus)) ||
          (store.state.userRole === 'assessor' &&
            (assessmentFin.status === 'assessed' || assrOnly.includes(assessmentFin.reportStatus)));

        const assessmentShouldNotBeIncluded =
          (filter === 'todo' && !qualifiesAsTodoReport) ||
          (filter === 'drafts' && assessmentFin.reportStatus !== 'draft') ||
          (filter === 'sent' && assrOnly.includes(assessmentFin.reportStatus));

        if (assessmentShouldNotBeIncluded) return;

        events.push(assessmentFin);
      },
    );

    if (filter === 'drafts')
      events = events.filter((assessment) => {
        if (assessment.reportId) {
          assessment.status = 'draft';
          return assessment;
        }
      });

    mode === 'interviews'
      ? store.commit('setInterviews', events)
      : store.commit('setReports', events);
  },

  async getAssessmentStatus({
    self,
    assessmentId,
    inModal,
  }: {
    self: any;
    assessmentId: string;
    inModal?: boolean;
  }): Promise<void> {
    store.commit('setAssessmentStatusPending', true);

    const response = await requestAPI({
      url: `/api/v1/assessments/${assessmentId}/status`,
      method: 'GET',
    });

    if (response.ok) {
      const data = await response.json();
      store.commit('setAssessmentStatus', data);
    } else if (response.status !== 401) {
      if (!inModal) {
        self.$message.error(self.$t('accessForbidden'));
        router.go(-1);
      } else self.$message.error(self.$t('someErrorOccurred'));
    }

    store.commit('setAssessmentStatusPending', false);
  },

  async getAssessmentMeeting({
    self,
    assessmentId,
  }: {
    self: any;
    assessmentId: string;
  }): Promise<void> {
    store.commit('setMeetingDetailsPending', true);

    const response = await requestAPI({
      url: `/api/v1/assessments/${assessmentId}/meeting`,
      method: 'GET',
    });

    if (response.ok) {
      const data = await response.json();
      store.commit('setMeetingDetails', data);
    } else if (response.status !== 401) {
      store.state.userRole === 'candidate'
        ? null
        : self.$message.error(self.$t('someErrorOccurred'));
    }

    store.commit('setMeetingDetailsPending', false);
  },

  // FIXME: For later
  async getReportStatus(reportId: string): Promise<string> {
    if (store.state.userRole === 'client') return '';
    const response = await requestAPI({ url: `/api/v1/reports/${reportId}`, method: 'GET' });

    if (response.ok) {
      const data = await response.json();
      return data.status;
    }

    return '';
  },

  async getAssessmentDates({
    self,
    dateFrom,
    dateTo,
  }: {
    self: any;
    dateFrom: string;
    dateTo: string;
  }): Promise<void> {
    store.commit('setAssessmentDatesPending', true);

    const response = await requestAPI({
      url: `/api/v1/assessors/me/assessment-dates?assessment_date[from]=${dateFrom}&assessment_date[to]=${dateTo}`,
      method: 'GET',
    });

    if (response.ok) {
      const data = await response.json();
      store.commit('setAssessmentDates', data);
    } else if (response.status !== 401) self.$message.error(self.$t('someErrorOccurred'));

    store.commit('setAssessmentDatesPending', false);
  },

  async getCandidateAssessments({
    self,
    candidateId,
  }: {
    self: any;
    candidateId: string;
  }): Promise<void> {
    const response = await requestAPI({
      url: `/api/v1/candidates/${candidateId}`,
      method: 'GET',
    });
    if (response.ok) {
      const data = await response.json();
      const paths = data.assessment_requests;
      const assessmentIds = paths.map((path) => {
        const match = path.match(/\/api\/v1\/assessment-requests\/(.+)$/);
        return match ? match[1] : null;
      });
      store.commit('setTrainingCandidateAssessments', assessmentIds);
    } else {
      self.$message.error(self.$t('someErrorOccurred'));
    }
  },

  async getTimelineData({
    self,
    assessmentId,
    inModal,
  }: {
    self: any;
    assessmentId: string;
    inModal: boolean;
  }): Promise<void> {
    store.commit('setTimelineDataPending', true);

    const response = await requestAPI({
      url: `/api/v1/assessments/${assessmentId}/timeline`,
      method: 'GET',
    });

    if (response.ok) {
      const data = await response.json();
      const containsTests = data.elements.some(
        (element: any) =>
          element.sub_elements &&
          element.sub_elements.some((subElement: any) => subElement.type === 'cebir_event'),
      );
      if (containsTests) {
        const testsElement = {
          type: 'basic',
          data: { status: 'tests' },
        };
        data.elements.push(testsElement);
      }
      store.commit('setTimelineData', data.elements);
    } else if (response.status !== 401) {
      if (!inModal) {
        self.$message.error(self.$t('accessForbidden'));
        router.go(-1);
      } else self.$message.error(self.$t('someErrorOccurred'));
    }

    store.commit('setTimelineDataPending', false);
  },
  async getNumberOfReports(self: any): Promise<void> {
    const statuses = 'status[]=published';
    const resp = await requestAPI({
      method: 'GET',
      url: `/api/v1/assessments?${statuses}`,
    });
    if (resp.ok) {
      const data = await resp.json();
      const numberOfOverallReports = data.length;
      store.commit('setOverallNumberOfReports', numberOfOverallReports);
    } else if (resp.status !== 401) self.$message.error(self.$t('someErrorOccurred'));
  },
  async checkCandidateOnTrainingPathAssessments(self: any): Promise<void> {
    const response = await requestAPI({
      url: `/api/v1/assessments`,
      method: 'GET',
    });
    if (response.ok) {
      const numberOfCandidateAssessments = await response.json();
      const candidateHasAssessments = numberOfCandidateAssessments.length > 0;
      if (candidateHasAssessments) {
        store.commit('setNumberOfCandidateAssessments', numberOfCandidateAssessments.length);
      }
    } else if (response.status !== 401) {
      self.$message.error(self.$t('someErrorOccurred'));
    }
  },
};

export default assessmentsService;
